import { TooltipDirective } from '../../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../../componentes/botones';
import {
  DtCardComponent,
  DtEstadoComponent,
  TonoEstado,
} from '../../../../../../componentes/detalle/detalle.components';
import { DatePipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription, from, map, switchMap } from 'rxjs';
import {
  EstadoDownlinkJob,
  IComando,
  IConfigDeseada,
  IDiffConfig,
  IDownlinkJob,
  IEstadoComando,
  IIntentoDownlink,
  IFilter,
  IQueryParam,
  OrigenDownlinkJob,
} from 'modelos/src';
import { ComandosService } from '../../../../../../auxiliares/servicios/http/comandos.service';
import { MetricasCronService } from '../../../../../../auxiliares/servicios/http/metricas-cron.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';
import { ILuminariaExtendida } from '../../../luminarias.types';
import { ConfigDeseadaDialogComponent } from './config-deseada-dialog/config-deseada-dialog.component';

// Mapa puerto → aspecto de la config deseada. Espeja puertoDeCampo/puertoGetDeCampo
// del reconciliador. SET = correctores; GET = consultas de esa sección.
const ASPECTO_PUERTO_ACTIS: Record<number, string> = {
  10: 'Modo encendido/apagado',
  12: 'Reloj astronómico',
  13: 'Fotocélula',
  14: 'Coordenadas',
  43: 'Configuración de reportes',
  44: 'Configuración de ACK',
  20: 'Dimming · perfiles habilitados',
  29: 'Dimming · nivel por defecto',
  21: 'Dimming · todos los días 1',
  22: 'Dimming · todos los días 2',
  23: 'Dimming · días de semana 1',
  24: 'Dimming · días de semana 2',
  25: 'Dimming · día especial 1',
  26: 'Dimming · día especial 2',
  27: 'Dimming · día especial 3',
  28: 'Dimming · día especial 4',
  15: 'Consulta de estado',
  45: 'Consulta de reportes',
  30: 'Consulta de dimming',
};
const ASPECTO_PUERTO_GPE: Record<number, string> = {
  100: 'Configuración general',
  112: 'Configuración de calendario',
  114: 'Configuración de calendario', // auto-get del calendario (mismo aspecto que el SET 112)
};

// Puerto de SET corrector → puerto del GET que lo verifica (bloque). El
// reconciliador coalesce los auto-gets: VARIOS correctores que caen en el mismo
// bloque comparten UN solo AutoGET (adjuntado al último set del bloque). Espeja
// `puertoGetDeCampo` de gestion-api-gestion/.../reconciliacion/service.ts.
const PUERTO_GET_DE_SET_ACTIS: Record<number, number> = {
  10: 15, // modo encendido/apagado
  12: 15, // reloj astronómico
  13: 15, // fotocélula
  14: 15, // coordenadas
  43: 45, // configuración de reportes
  44: 45, // configuración de ACK
  20: 30, // dimming · perfiles
  21: 30,
  22: 30,
  23: 30,
  24: 30,
  25: 30,
  26: 30,
  27: 30,
  28: 30,
  29: 30,
};
const PUERTO_GET_DE_SET_GPE: Record<number, number> = {
  100: 100, // configuración general
  112: 114, // calendario → auto-get DL 114 (job que verifica; su respuesta llega por UL 113)
};

// Tipo de paso dentro de una rama. Determina el color de PROCESO del nodo.
type TipoPaso = 'set' | 'autoget' | 'bootstrap';

// Un paso = un downlink job + su comando del dispositivo (join por idComando).
// El job cuenta la historia del TRANSPORTE (cola/BullMQ); el comando cuenta la
// del DISPOSITIVO (recibido/ejecutado). Se muestran como doble-carril.
interface PasoCronologia {
  job: IDownlinkJob;
  comando?: IComando;
  tipo: TipoPaso;
  aspecto: string;
  // Discrepancias (esperado vs real) que este SET corrige. Solo se llenan en el
  // ciclo de reconciliación MÁS RECIENTE (los diffs solo viven, y se sobrescriben,
  // en IConfigDeseada.diffs — no hay snapshot histórico por ciclo).
  diffs?: IDiffConfig[];
}

// Una rama = un TIPO de configuración (bloque de get) corregido en un ciclo:
// SET(s) corrector(es) + su AutoGET encadenado → respuesta. Ej: dimerizado
// (get30), config general (get45), modo/reloj/foto/coords (get15). En bootstrap
// (ConsultaConfig) la rama es un único GET puro. Cada rama se muestra separada.
interface RamaCronologia {
  aspecto: string;
  pasos: PasoCronologia[];
}

// Agrupación de jobs por ejecución (batch idEjecucion). Cada ejecución = un
// "ciclo" de reconciliación/consulta (una corrida del cron). Dentro del ciclo se
// separan por TIPO de config (ramas por bloque de get). Cada corrida del
// reconciliador es un idEjecucion distinto → ciclo aparte.
interface EjecucionCronologia {
  clave: string;
  // `fecha` = última actividad del ciclo (job más reciente). `fechaInicio` =
  // inicio del plan (job más viejo). El plan avanza ACK-gated en olas, así que
  // ambas difieren: el header muestra las dos.
  fecha?: string;
  fechaInicio?: string;
  origen?: OrigenDownlinkJob;
  objetivoNombre?: string;
  objetivoNivel?: string;
  ramas: RamaCronologia[];
  ok: number;
  fallo: number;
  pendiente: number;
}

// Glosario de estados (se muestra en el panel de ayuda desplegable).
interface ItemGlosario {
  estado: string;
  clase: string;
  desc: string;
}

// Un paso en el mini-stepper de un riel (cola o dispositivo). Representa un
// estado por el que pasó (o se infiere que pasó) el job/comando.
interface PasoEstado {
  estado: string;
  clase: string; // e-ok | e-fallo | e-pendiente | e-cancelado
  fecha?: string; // ISO si se conoce el timestamp de la transición
  actual: boolean; // es el estado vigente
  inferido?: boolean; // no hay timestamp; se deduce de la máquina de estados
}

// Cronología (git-graph) de la aplicación de un perfil efectivo: tronco de
// ciclos de reconciliación, ramas por aspecto (SET → AutoGET → respuesta),
// reintentos de transporte y de reconciliación, y el doble estado cola+dispositivo.
@Component({
  selector: 'app-cronologia-perfil',
  standalone: true,
  imports: [TooltipDirective, 
    DatePipe,
    MatIconModule,
    BOTONES,
    DtCardComponent,
    DtEstadoComponent,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './cronologia-perfil.component.html',
  styleUrls: ['./cronologia-perfil.component.scss'],
})
export class CronologiaPerfilComponent implements OnChanges, OnDestroy {
  private lista2 = inject(Lista2Service);
  private comandosService = inject(ComandosService);
  private formularios = inject(FormularioDialogService);
  private metricasCron = inject(MetricasCronService);

  // Deadline de ACK del paso (s). Es el umbral con el que se juzga la espera en
  // fila de cada envío: si la espera lo supera, el paso venció antes de que el
  // downlink saliera y el ACK legítimo del dispositivo aterrizó en un paso ya
  // cerrado — o sea que el "no contestó" es nuestro, no del equipo. Se lee de la
  // config real del cron; el 15 es sólo el fallback si el endpoint no responde.
  deadlineAckSeg = 15;

  // Polling de actualización en vivo (segundos), análogo a los listados.
  private readonly INTERVALO_SEG = 15;
  private sub?: Subscription;
  // Para no pisar la interacción del usuario (acordeón) en cada refresco.
  private inicializado = false;

  @Input() luminaria: ILuminariaExtendida | null = null;
  @Input() configDeseada: IConfigDeseada | null = null;

  cargando = signal(false);
  ejecuciones = signal<EjecucionCronologia[]>([]);
  mostrarAyuda = signal(false);
  // Paginación incremental del tronco: la línea de tiempo es potencialmente
  // infinita, así que se muestran de a tandas. Empieza en PAGINA y crece con
  // "Mostrar más"; se resetea en cada (re)carga de luminaria.
  private readonly PAGINA = 8;
  visibles = signal(this.PAGINA);
  // Claves de los ciclos expandidos (acordeón). Por defecto solo el más reciente.
  expandidos = signal<Set<string>>(new Set());
  // Nodos con el panel de discrepancias abierto (bajo demanda, por job._id).
  diffsAbiertos = signal<Set<string>>(new Set());

  // ─── glosario para el panel de ayuda ────────────────────────

  readonly estadosCola: ItemGlosario[] = [
    {
      estado: 'En fila',
      clase: 'e-pendiente',
      desc: 'Creado, esperando turno en la fila BullMQ.',
    },
    {
      estado: 'Programado',
      clase: 'e-pendiente',
      desc: 'En fila con delay (Ej: un autoget posterior a un set o un comando reprogramado por un rate limiter.',
    },
    {
      estado: 'Enviando',
      clase: 'e-pendiente',
      desc: 'El worker tomó el job y lo está despachando.',
    },
    {
      estado: 'Enviado',
      clase: 'e-ok',
      desc: 'Despachado a Chirpstack/NS correctamente.',
    },
    {
      estado: 'Confirmado',
      clase: 'e-ok',
      desc: 'Asociado a un comando con estado terminal favorable.',
    },
    {
      estado: 'Descartado',
      clase: 'e-cancelado',
      desc: 'Dispositivo offline o NS sin sesión: no se reintenta.',
    },
    {
      estado: 'Cancelado',
      clase: 'e-cancelado',
      desc: 'Reemplazado por un job más nuevo con la misma clave.',
    },
    {
      estado: 'Fallido',
      clase: 'e-fallo',
      desc: 'Error técnico tras agotar los reintentos de transporte.',
    },
  ];

  readonly estadosDispositivo: ItemGlosario[] = [
    {
      estado: 'En fila',
      clase: 'e-pendiente',
      desc: 'En la fila del NS, todavía no enviado al dispositivo (dura muy poco).',
    },
    {
      estado: 'Enviado',
      clase: 'e-pendiente',
      desc: 'Despachado hacia el dispositivo (estado inicial).',
    },
    {
      estado: 'Recibido',
      clase: 'e-ok',
      desc: 'El dispositivo confirmó la recepción (ACK).',
    },
    {
      estado: 'Ejecutado',
      clase: 'e-ok',
      desc: 'El dispositivo aplicó el comando (Hubo uplink de respuesta que efectivamente cambió la configuración real).',
    },
    {
      estado: 'Ack false',
      clase: 'e-pendiente',
      desc: 'Llegó ACK con acknowledged false (el comando pudo aplicarse igual).',
    },
    {
      estado: 'No Ejecutado',
      clase: 'e-fallo',
      desc: 'Pasó demasiado tiempo y el dispositivo no confirmó recepción o hubo respuesta pero la configuración reportada no es la que se solicitó.',
    },
    {
      estado: 'Descartado',
      clase: 'e-cancelado',
      desc: 'Descartado por el NS (offline / expiró la ventana).',
    },
  ];

  readonly procesos = [
    {
      clase: 'p-consulta',
      label: 'Consulta inicial',
      desc: 'GET puro de consulta (sin reintento, no escribe config deseada).',
    },
    {
      clase: 'p-reconciliacion',
      label: 'Reconciliación',
      desc: 'Ciclo de comparación de configuraciones: deseada vs real.',
    },
    {
      clase: 'p-set',
      label: 'SET corrector',
      desc: 'Downlink que corrige un campo en discrepancia.',
    },
    {
      clase: 'p-autoget',
      label: 'AutoGET',
      desc: 'GET encadenado tras el SET para refrescar la config real.',
    },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['luminaria']) {
      void this.cargarDeadline();
      this.cargar();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private get idDispositivo(): string | undefined {
    return this.luminaria?.dispositivo?._id;
  }

  // Suscripción con polling (vía Lista2Service) para refresco en vivo, igual que
  // los listados. Cada tick trae los downlinks y, encadenado, sus comandos.
  private cargar(): void {
    this.sub?.unsubscribe();
    const idDispositivo = this.idDispositivo;
    if (!idDispositivo) {
      this.ejecuciones.set([]);
      return;
    }
    this.cargando.set(true);
    this.inicializado = false;
    this.visibles.set(this.PAGINA);

    // Solo los downlinks que aplican el perfil efectivo: reconciliador
    // (correctores), consultador (bootstrap GETs) y auto-get encadenado.
    // Se excluyen los 'Manual' (acciones puntuales del usuario).
    const filter = {
      idDispositivoLorawan: idDispositivo,
      origen: { $in: ['Reconciliacion', 'ConsultaConfig', 'AutoGet'] },
    } as unknown as IFilter<IDownlinkJob>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      limit: 100,
      includeChildren: true,
    };

    this.sub = this.lista2
      .downlinkJobs(query, this.INTERVALO_SEG)
      .pipe(
        switchMap((res) => {
          const jobs = res?.datos ?? [];
          return from(this.cargarComandos(jobs)).pipe(
            map((comandos) => ({ jobs, comandos })),
          );
        }),
      )
      .subscribe({
        next: ({ jobs, comandos }) => {
          const ejes = this.agrupar(jobs, comandos);
          this.ejecuciones.set(ejes);
          // Acordeón: expandir el ciclo más reciente SOLO en la primera carga;
          // los refrescos posteriores no pisan lo que el usuario abrió/cerró.
          if (!this.inicializado) {
            this.expandidos.set(
              ejes[0]?.clave ? new Set([ejes[0].clave]) : new Set(),
            );
            this.inicializado = true;
          }
          this.cargando.set(false);
        },
        error: (e) => {
          console.error('Error cargando cronología de perfil:', e);
          this.ejecuciones.set([]);
          this.cargando.set(false);
        },
      });
  }

  // Join con los comandos del dispositivo. Recolecta los idComando de cada job
  // (y de cada intento de transporte) y los trae en un solo batch para mostrar
  // el estado REAL del dispositivo (Recibido/Ejecutado/...), no solo el de la cola.
  private async cargarComandos(
    jobs: IDownlinkJob[],
  ): Promise<Map<string, IComando>> {
    const ids = new Set<string>();
    for (const j of jobs) {
      if (j.idComando) ids.add(j.idComando);
      for (const it of j.intentosLog ?? []) {
        if (it.idComando) ids.add(it.idComando);
      }
    }
    if (!ids.size) return new Map();
    const lista = Array.from(ids);
    const filter = { _id: { $in: lista } } as unknown as IFilter<IComando>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      limit: lista.length,
      // Necesario para que un cliente ANCESTRO vea los comandos del cliente
      // (descendiente) de la luminaria; si no, el riel "dispositivo" queda vacío.
      includeChildren: true,
    };
    const res = await this.comandosService.getFiltered(query);
    const mapa = new Map<string, IComando>();
    for (const c of res?.datos ?? []) {
      if (c._id) mapa.set(c._id, c);
    }
    return mapa;
  }

  // Agrupa los jobs por idEjecucion (ciclo) y, dentro de cada ciclo, arma las
  // ramas: cada SET corrector con su AutoGET encadenado (mismo idEjecucion,
  // emparejado por proximoGet.puerto). Los GET de bootstrap quedan como ramas sueltas.
  private agrupar(
    jobs: IDownlinkJob[],
    comandos: Map<string, IComando>,
  ): EjecucionCronologia[] {
    const mapa = new Map<string, EjecucionCronologia>();
    const jobsPorCiclo = new Map<string, IDownlinkJob[]>();

    for (const job of jobs) {
      const clave = job.idEjecucion || job._id || job.fechaCreacion || 'sin-id';
      if (!mapa.has(clave)) {
        mapa.set(clave, {
          clave,
          fecha: job.fechaCreacion,
          origen: job.origen,
          objetivoNombre: job.objetivo?.nombre,
          objetivoNivel: job.objetivo?.nivel,
          ramas: [],
          ok: 0,
          fallo: 0,
          pendiente: 0,
        });
        jobsPorCiclo.set(clave, []);
      }
      jobsPorCiclo.get(clave)!.push(job);
      const grupo = mapa.get(clave)!;
      if (this.esExito(job.estado)) grupo.ok++;
      else if (this.esFallo(job.estado)) grupo.fallo++;
      else grupo.pendiente++;
      // El ciclo nace como Reconciliación si tiene al menos un SET corrector.
      if (job.origen === 'Reconciliacion') grupo.origen = 'Reconciliacion';
    }

    for (const [clave, grupo] of mapa) {
      const delCiclo = jobsPorCiclo.get(clave)!;
      delCiclo.sort((a, b) =>
        (a.fechaCreacion ?? '').localeCompare(b.fechaCreacion ?? ''),
      );
      // Ordenado asc: primero = inicio del plan, último = actividad más reciente.
      grupo.fechaInicio = delCiclo[0]?.fechaCreacion;
      grupo.fecha = delCiclo[delCiclo.length - 1]?.fechaCreacion;
      grupo.ramas = this.armarRamas(delCiclo, comandos);
    }

    const ejes = Array.from(mapa.values()).sort((a, b) =>
      (b.fecha ?? '').localeCompare(a.fecha ?? ''),
    );
    this.adjuntarDiffsActuales(ejes);
    return ejes;
  }

  // Adjunta las discrepancias (esperado vs real) de la ÚLTIMA comparación al
  // ciclo de reconciliación más reciente. Se matchean por puertoDownlink contra
  // cada SET corrector. No hay snapshot histórico: solo aplica al ciclo vigente.
  private adjuntarDiffsActuales(ejes: EjecucionCronologia[]): void {
    const diffs = this.configDeseada?.diffs ?? [];
    if (!diffs.length) return;
    const reciente = ejes.find((e) => e.origen === 'Reconciliacion');
    if (!reciente) return;
    for (const rama of reciente.ramas) {
      for (const paso of rama.pasos) {
        if (paso.tipo !== 'set') continue;
        paso.diffs = diffs.filter(
          (d) =>
            d.puertoDownlink === paso.job.puerto && !this.diffOculto(d.campo),
        );
      }
    }
  }

  // Campos que NO se muestran en las discrepancias: el flag manual de
  // encendido/apagado no es config reconciliable de interés para la cronología.
  private diffOculto(campo: string): boolean {
    return /^modo(Encendido|Apagado)\.manual\b/.test(campo);
  }

  // Agrupa los jobs del ciclo por TIPO de config (bloque de get que los verifica).
  // Cada bloque = una rama que se muestra separada: dimerizado (get30), config
  // general (get45), modo/reloj/foto/coords (get15), etc. Los SET del bloque +
  // su AutoGET encadenado forman la cadena secuencial de esa rama.
  private armarRamas(
    jobs: IDownlinkJob[],
    comandos: Map<string, IComando>,
  ): RamaCronologia[] {
    const sets = jobs.filter((j) => j.origen === 'Reconciliacion');
    const gets = jobs.filter((j) => j.origen !== 'Reconciliacion');
    const usados = new Set<string>();
    const ramas: RamaCronologia[] = [];

    // Agrupa los SET correctores por el puerto-get que los verifica (bloque).
    // Varios correctores del mismo bloque comparten UN solo AutoGET, así que la
    // rama es: [SET, SET, ...] → AutoGET → resultado.
    const bloques = new Map<number, IDownlinkJob[]>();
    const setsSinBloque: IDownlinkJob[] = [];
    for (const set of sets) {
      const gp = this.puertoGetDeSet(set);
      if (gp == null) {
        setsSinBloque.push(set);
        continue;
      }
      (bloques.get(gp) ?? bloques.set(gp, []).get(gp)!).push(set);
    }

    for (const [gp, correctores] of bloques) {
      correctores.sort((a, b) =>
        (a.fechaCreacion ?? '').localeCompare(b.fechaCreacion ?? ''),
      );

      // Dimming ACTIS (puerto-get 30): NO se coalesce. getDimProfiles pide UN
      // perfilN por comando, así que el reconciliador emite un get30 por perfil
      // seteado. Cada SET se parea 1:1 con SU autoget (match por payload, que
      // codifica el perfilN / hab / dimDefault). Espeja adjuntarAutoGetActis.
      if (gp === 30) {
        for (const set of correctores) {
          const pasos = [this.armarPaso(set, 'set', comandos)];
          const esperado = this.payloadGet30DeSet(set);
          const autoget = gets.find(
            (g) =>
              !usados.has(g._id ?? '') &&
              g.puerto === 30 &&
              (g.payload ?? '').toLowerCase() === esperado,
          );
          if (autoget) {
            usados.add(autoget._id ?? '');
            pasos.push(this.armarPaso(autoget, 'autoget', comandos));
          }
          ramas.push({ aspecto: this.aspectoConfig(set), pasos });
        }
        continue;
      }

      const pasos = correctores.map((s) => this.armarPaso(s, 'set', comandos));
      // AutoGET único del bloque: mismo ciclo, puerto = puerto-get del bloque.
      const autoget = gets.find(
        (g) => !usados.has(g._id ?? '') && g.puerto === gp,
      );
      if (autoget) {
        usados.add(autoget._id ?? '');
        pasos.push(this.armarPaso(autoget, 'autoget', comandos));
      }
      // Header del bloque = aspecto(s) de CONFIG que se aplican (lo que cambia el
      // SET), no el puerto-get. Coherente ACTIS/GPE: un SET nunca se rotula como
      // "Consulta de X". Si el bloque agrupa varios aspectos (ACTIS: varios sets
      // comparten un get), se listan deduplicados. El nodo del auto-get muestra
      // su propio detalle ("Auto-get pto X" + sub-tag de consulta).
      const tipoDisp = correctores[0]?.tipoDispositivo;
      const aspectosConfig = [
        ...new Set(
          correctores.map((s) => this.aspectoConfig(s)).filter(Boolean),
        ),
      ];
      ramas.push({
        aspecto:
          aspectosConfig.join(' · ') || this.aspectoPorPuerto(gp, tipoDisp),
        pasos,
      });
    }

    // SET sin bloque-get (no encadenan AutoGET) → rama individual.
    for (const set of setsSinBloque) {
      ramas.push({
        aspecto: this.aspectoConfig(set),
        pasos: [this.armarPaso(set, 'set', comandos)],
      });
    }

    // GET sueltos (bootstrap de Consulta, o autoget sin bloque) → rama propia.
    for (const g of gets) {
      if (usados.has(g._id ?? '')) continue;
      const tipo: TipoPaso = g.origen === 'AutoGet' ? 'autoget' : 'bootstrap';
      ramas.push({
        aspecto: this.aspectoConfig(g),
        pasos: [this.armarPaso(g, tipo, comandos)],
      });
    }

    return ramas.sort((a, b) =>
      (a.pasos[0]?.job.fechaCreacion ?? '').localeCompare(
        b.pasos[0]?.job.fechaCreacion ?? '',
      ),
    );
  }

  // Payload (hex) del get30 que verifica a este SET de dimming. Espeja el
  // armado de adjuntarAutoGetActis / buildGetDimProfilesActis del backend:
  // pto20 → hab (0x80), pto29 → dimDefault (0x40), pto21-28 → perfilN en los 4
  // LSB (perfil 1→01 … 7→07, 8→08).
  private payloadGet30DeSet(set: IDownlinkJob): string {
    const p = set.puerto;
    if (p === 20) return '80';
    if (p === 29) return '40';
    if (p != null && p >= 21 && p <= 28) {
      const byte = (p - 20) & 0x0f;
      return byte.toString(16).padStart(2, '0');
    }
    return '';
  }

  // Puerto del GET que verifica a este SET corrector (bloque al que pertenece).
  private puertoGetDeSet(job: IDownlinkJob): number | undefined {
    if (job.puerto == null) return undefined;
    const mapa =
      job.tipoDispositivo === 'Luminaria GPE'
        ? PUERTO_GET_DE_SET_GPE
        : job.tipoDispositivo === 'Luminaria ACTIS FING'
          ? PUERTO_GET_DE_SET_ACTIS
          : null;
    return mapa?.[job.puerto];
  }

  private armarPaso(
    job: IDownlinkJob,
    tipo: TipoPaso,
    comandos: Map<string, IComando>,
  ): PasoCronologia {
    return {
      job,
      tipo,
      aspecto: this.aspectoConfig(job),
      comando: job.idComando ? comandos.get(job.idComando) : undefined,
    };
  }

  // ─── clasificación: estado de la COLA (downlink job) ─────────

  esExito(estado: EstadoDownlinkJob): boolean {
    return estado === 'Confirmado' || estado === 'Enviado';
  }

  esFallo(estado: EstadoDownlinkJob): boolean {
    return estado === 'Fallido' || estado === 'Descartado';
  }

  esPendiente(estado: EstadoDownlinkJob): boolean {
    return (
      estado === 'En cola' || estado === 'Programado' || estado === 'Enviando'
    );
  }

  // En luminarias se habla de "fila", nunca de "cola" (el valor del backend
  // sigue siendo 'En cola').
  estadoLabel(estado?: string): string {
    return estado === 'En cola' || estado === 'En Cola'
      ? 'En fila'
      : (estado ?? '');
  }

  estadoClase(estado: EstadoDownlinkJob): string {
    if (this.esExito(estado)) return 'e-ok';
    if (this.esFallo(estado)) return 'e-fallo';
    if (estado === 'Cancelado') return 'e-cancelado';
    return 'e-pendiente';
  }

  estadoIcono(estado: EstadoDownlinkJob): string {
    switch (estado) {
      case 'Confirmado':
        return 'check_circle';
      case 'Enviado':
        return 'send';
      case 'Fallido':
        return 'error';
      case 'Descartado':
        return 'block';
      case 'Cancelado':
        return 'cancel';
      case 'Enviando':
        return 'sync';
      case 'Programado':
        return 'event';
      default:
        return 'pending';
    }
  }

  // ─── clasificación: estado del DISPOSITIVO (comando) ─────────

  comandoClase(estado?: IEstadoComando): string {
    if (!estado) return 'e-na';
    // Recibido (ACK) ya es un progreso favorable → verde, igual que Ejecutado.
    if (estado === 'Ejecutado' || estado === 'Recibido') return 'e-ok';
    // "No Recibido" (ack false) no es una falla dura: el device pudo aplicar el
    // comando igual; se muestra como advertencia (naranja), no en rojo.
    if (estado === 'No Recibido') return 'e-pendiente';
    if (estado === 'No Ejecutado' || estado === 'Descartado') return 'e-fallo';
    return 'e-pendiente'; // En Cola, Enviado
  }

  // Etiqueta a mostrar para el estado del dispositivo. "No Recibido" se rotula
  // "ack false" (no llegó acuse de recibo); el resto se muestra tal cual.
  comandoLabel(estado?: IEstadoComando): string {
    if (estado === 'No Recibido') return 'Ack false';
    if (estado === 'En Cola') return 'En fila';
    return estado ?? '';
  }

  comandoIcono(estado?: IEstadoComando): string {
    switch (estado) {
      case 'Ejecutado':
        return 'task_alt';
      case 'Recibido':
        return 'check_circle';
      case 'Enviado':
        return 'send';
      case 'En Cola':
        return 'pending';
      case 'No Recibido':
      case 'No Ejecutado':
        return 'cancel';
      case 'Descartado':
        return 'block';
      default:
        return 'question_mark';
    }
  }

  // ─── steppers: TODOS los estados recorridos por cada riel ────

  // Riel COLA: la secuencia se reconstruye de los timestamps del job (cada
  // fecha presente = una transición alcanzada). El estado vigente cierra la
  // cadena (incluido un terminal negativo Descartado/Cancelado/Fallido).
  colaPasos(job: IDownlinkJob): PasoEstado[] {
    const pasos: PasoEstado[] = [];
    const add = (estado: string, fecha?: string, inferido = false) =>
      pasos.push({
        estado,
        clase: this.estadoClase(estado as EstadoDownlinkJob),
        fecha,
        actual: false,
        inferido,
      });

    // Un job con fechaProgramada NACIÓ 'Programado' (delay: auto-get, rebote de
    // rate-limit, o batch escalonado). NUNCA pasó por 'En cola' → no lo antepongas.
    // Solo los jobs sin delay arrancan en 'En cola'.
    if (job.fechaProgramada) add('Programado', job.fechaProgramada);
    else add('En cola', job.fechaCreacion);
    if (job.fechaEnviado) add('Enviado', job.fechaEnviado);
    if (job.fechaConfirmado) add('Confirmado', job.fechaConfirmado);

    // El estado vigente: si todavía no quedó representado, se agrega al final
    // (Enviando transitorio, o un terminal negativo sin timestamp propio).
    if (!pasos.some((p) => p.estado === job.estado)) {
      add(job.estado, undefined, true);
    }
    const actual = [...pasos].reverse().find((p) => p.estado === job.estado);
    if (actual) actual.actual = true;
    return pasos;
  }

  // Riel DISPOSITIVO: el comando solo guarda el estado vigente (no historial),
  // así que se infiere el prefijo de la máquina de estados hasta el actual.
  dispositivoPasos(estado?: IEstadoComando): PasoEstado[] {
    if (!estado) return [];
    const happy: IEstadoComando[] = [
      'En Cola',
      'Enviado',
      'Recibido',
      'Ejecutado',
    ];
    const idx = happy.indexOf(estado);
    const mk = (e: IEstadoComando, actual: boolean): PasoEstado => ({
      estado: e,
      clase: this.comandoClase(e),
      actual,
      inferido: !actual, // los previos son deducidos (sin timestamp propio)
    });
    if (idx >= 0) {
      return happy.slice(0, idx + 1).map((e, i) => mk(e, i === idx));
    }
    // Terminal negativo: se asume el camino previo hasta donde se cortó.
    const previos: IEstadoComando[] =
      estado === 'Descartado' ? ['En Cola'] : ['En Cola', 'Enviado'];
    return [...previos.map((e) => mk(e, false)), mk(estado, true)];
  }

  // ─── proceso (color de la rama/nodo) ────────────────────────

  procesoClase(paso: PasoCronologia): string {
    switch (paso.tipo) {
      case 'set':
        return 'p-set';
      case 'autoget':
        return 'p-autoget';
      default:
        return 'p-consulta';
    }
  }

  cicloClase(eje: EjecucionCronologia): string {
    return eje.origen === 'ConsultaConfig' ? 'p-consulta' : 'p-reconciliacion';
  }

  // SET(s) de la rama: los downlinks que ESCRIBEN config. Vacío en ramas de
  // sola consulta (bootstrap de ConsultaConfig, o autoget sin SET pareado).
  private setsDeRama(rama: RamaCronologia): PasoCronologia[] {
    return rama.pasos.filter((p) => p.tipo === 'set');
  }

  // ¿La rama es de sola consulta (no escribe config)?
  ramaSoloConsulta(rama: RamaCronologia): boolean {
    return this.setsDeRama(rama).length === 0;
  }

  // ¿Quedó aplicada la config de la rama?
  //
  // El veredicto lo dan los SET, NO el último paso: el AutoGET puede quedar
  // "Ejecutado" (llegó el eco de config) y aun así el SET quedar "No Ejecutado"
  // porque el payload del eco no coincidía con lo enviado (el device NO aplicó).
  // Mirar el último paso daba el falso "Aplicado por uplink" en ese caso.
  // En ramas de sola consulta el veredicto es del GET (consulta respondida).
  ramaResuelta(rama: RamaCronologia): boolean {
    const sets = this.setsDeRama(rama);
    if (!sets.length) {
      const ultimo = rama.pasos[rama.pasos.length - 1];
      return ultimo?.comando?.estado === 'Ejecutado';
    }
    return sets.every((p) => p.comando?.estado === 'Ejecutado');
  }

  // ¿Algún SET quedó rechazado por el dispositivo (No Ejecutado)? Se muestra
  // explícito: sin esto la rama no mostraba nada y se leía como "en curso".
  ramaNoAplicada(rama: RamaCronologia): boolean {
    const sets = this.setsDeRama(rama);
    return (
      sets.length > 0 && sets.some((p) => p.comando?.estado === 'No Ejecutado')
    );
  }

  // Motivo del rechazo (respuesta del comando) para el tooltip del badge.
  ramaMotivoNoAplicada(rama: RamaCronologia): string {
    const fallado = this.setsDeRama(rama).find(
      (p) => p.comando?.estado === 'No Ejecutado',
    );
    return (
      fallado?.comando?.respuesta ??
      'El dispositivo no aplicó la configuración enviada'
    );
  }

  // ¿La rama se dio por aplicada solo por el ACK del SET (sin eco de uplink que
  // lo confirme)? Feature aplicar-por-ACK: el device ackeó el frame y se asumió
  // aplicada la config directo desde el payload, sin esperar el auto-get. Se
  // muestra distinto ("aplicado sin confirmar") porque no hubo verificación por
  // uplink.
  ramaAplicadaPorAck(rama: RamaCronologia): boolean {
    const sets = this.setsDeRama(rama);
    return sets.some(
      (p) =>
        p.comando?.estado === 'Ejecutado' &&
        p.comando?.datosExtra?.['confirmadoPor'] === 'ack',
    );
  }

  // ─── clasificación de cada intento de transporte ────────────

  // ── Latencia y veredicto por envío ────────────────────────────────────────
  // El IDownlinkJob ya trae, por envío, cuánto esperó en NUESTRA fila y cuánto
  // tardó el despacho; y por paso, el veredicto con su fecha. Mostrarlo acá es
  // lo que evita tener que cruzar comandos a mano para entender por qué un SET
  // "no fue confirmado": muchas veces el downlink salió tarde y el paso ya
  // había vencido.
  private async cargarDeadline(): Promise<void> {
    try {
      const estado = await this.metricasCron.getEstado();
      const d = estado?.config?.secuenciador?.deadlineAckSec;
      if (d) this.deadlineAckSeg = d;
    } catch {
      /* se queda con el fallback */
    }
  }

  // Segundos → texto corto. Los valores útiles van de 0,1 s a minutos.
  fmtSeg(seg?: number): string {
    if (seg == null) return '';
    if (seg < 1) return `${seg.toFixed(2).replace('.', ',')} s`;
    if (seg < 90) return `${Math.round(seg)} s`;
    return `${(seg / 60).toFixed(1).replace('.', ',')} min`;
  }

  // La espera en la fila se comió el deadline del paso.
  esperaExcedida(it: IIntentoDownlink): boolean {
    return (it.esperaColaSeg ?? 0) > this.deadlineAckSeg;
  }

  tooltipEspera(it: IIntentoDownlink): string {
    const espera = this.fmtSeg(it.esperaColaSeg);
    const base =
      `Esperó ${espera} en la fila de envío (rate limit + espera de worker) ` +
      `y el sistema tardó ${this.fmtSeg(it.despachoSeg)} en despacharlo.`;
    return this.esperaExcedida(it)
      ? `${base} Supera el timeout de ACK del paso (${this.deadlineAckSeg} s): ` +
          `el paso podía vencer antes de que este downlink saliera.`
      : base;
  }

  // HUÉRFANO: el envío salió después de que su paso ya tenía veredicto. Lo cerró
  // el envío anterior mientras éste esperaba en la fila, así que nadie lo va a
  // confirmar: queda en 'No Recibido'/'No Ejecutado' aunque el dispositivo haya
  // respondido bien.
  esHuerfano(job: IDownlinkJob, it: IIntentoDownlink): boolean {
    if (!job.fechaVeredicto || !it.fecha) return false;
    return (
      new Date(it.fecha).getTime() > new Date(job.fechaVeredicto).getTime()
    );
  }

  veredictoLabel(v?: string): string {
    switch (v) {
      case 'ack':
        return 'Cerrado por ACK (fast-path)';
      case 'ack-no-aplicado':
        return 'ACK recibido, config no aplicada';
      case 'rechazado':
        return 'Rechazado por el dispositivo (payload descartado)';
      case 'eco':
        return 'Verificado por eco del dispositivo';
      case 'timeout':
        return 'Forzado: agotó los envíos sin respuesta';
      case 'descartado':
        return 'Descartado (offline / sin sesión)';
      case 'abortado':
        return 'Plan abortado';
      default:
        return 'Sin veredicto todavía';
    }
  }

  /** Etiqueta corta para el chip. La larga (veredictoLabel) va al tooltip:
   *  en una card de la cronología no entra y se partía en tres renglones. */
  veredictoLabelCorto(v?: string): string {
    switch (v) {
      case 'ack':
        return 'Por ACK';
      case 'ack-no-aplicado':
        return 'ACK sin aplicar';
      case 'rechazado':
        return 'Rechazado';
      case 'eco':
        return 'Verificado';
      case 'timeout':
        return 'Sin respuesta';
      case 'descartado':
        return 'Descartado';
      case 'abortado':
        return 'Abortado';
      default:
        return 'Sin veredicto';
    }
  }

  veredictoIcono(v?: string): string {
    switch (v) {
      case 'ack':
        return 'check_circle';
      case 'ack-no-aplicado':
        return 'warning';
      case 'rechazado':
        return 'cancel';
      case 'eco':
        return 'task_alt';
      case 'timeout':
        return 'timer_off';
      case 'descartado':
      case 'abortado':
        return 'block';
      default:
        return 'pending';
    }
  }

  veredictoClase(v?: string): string {
    if (v === 'ack' || v === 'eco') return 'e-ok';
    if (v === 'ack-no-aplicado') return 'e-pendiente';
    if (
      v === 'timeout' ||
      v === 'descartado' ||
      v === 'abortado' ||
      v === 'rechazado'
    )
      return 'e-fallo';
    return 'e-pendiente';
  }

  intentoClase(resultado: string): string {
    if (resultado === 'Enviado') return 'e-ok';
    if (resultado === 'Error' || resultado === 'Descartado') return 'e-fallo';
    return 'e-pendiente';
  }

  intentoIcono(resultado: string): string {
    switch (resultado) {
      case 'Enviado':
        return 'check_circle';
      case 'Descartado':
        return 'block';
      case 'Error':
        return 'error';
      default:
        return 'pending';
    }
  }

  // Aspecto de la configuración deseada al que corresponde el downlink. Es
  // INTRÍNSECO al comando (lo determina su puerto) e HISTÓRICO: todo job guarda
  // su puerto, así que aplica también a downlinks viejos, sin depender de los
  // diffs vigentes. El mapa puerto→aspecto espeja `puertoDeCampo`/`puertoGetDeCampo`
  // del reconciliador (gestion-api-gestion/.../reconciliacion/service.ts).
  aspectoConfig(job: IDownlinkJob): string {
    const persistido = job.datosExtra?.['aspecto'];
    if (typeof persistido === 'string' && persistido) return persistido;
    return this.aspectoPorPuerto(job.puerto, job.tipoDispositivo);
  }

  private aspectoPorPuerto(
    puerto: number | undefined,
    tipo: IDownlinkJob['tipoDispositivo'],
  ): string {
    if (puerto == null) return '';
    const mapa =
      tipo === 'Luminaria GPE'
        ? ASPECTO_PUERTO_GPE
        : tipo === 'Luminaria ACTIS FING'
          ? ASPECTO_PUERTO_ACTIS
          : null;
    return mapa?.[puerto] ?? '';
  }

  origenLabel(origen?: OrigenDownlinkJob): string {
    switch (origen) {
      case 'Reconciliacion':
        return 'Reconciliación';
      case 'Manual':
        return 'Manual';
      case 'AutoGet':
        return 'Auto-GET';
      case 'ConsultaConfig':
        return 'Consulta';
      default:
        return 'Sin origen';
    }
  }

  nivelLabel(nivel?: string): string {
    switch (nivel) {
      case 'luminaria':
        return 'Luminaria';
      case 'grupo':
        return 'Grupo';
      case 'puesta':
        return 'Puesta';
      case 'grupoPuesta':
        return 'Grupo de puestas';
      default:
        return nivel ?? '';
    }
  }

  // ─── paginación del tronco (línea de tiempo acotada) ────────

  // Ciclos efectivamente renderizados (la tanda visible del tronco).
  ejecucionesVisibles(): EjecucionCronologia[] {
    return this.ejecuciones().slice(0, this.visibles());
  }

  // Ciclos aún ocultos bajo el corte de paginación.
  restantes(): number {
    return Math.max(0, this.ejecuciones().length - this.visibles());
  }

  mostrarMas(): void {
    this.visibles.update((n) => n + this.PAGINA);
  }

  mostrarTodos(): void {
    this.visibles.set(this.ejecuciones().length);
  }

  // ¿Hay más ciclos mostrados que la tanda inicial? (para ofrecer colapsar)
  hayExpandidos(): boolean {
    return this.visibles() > this.PAGINA;
  }

  mostrarMenos(): void {
    this.visibles.set(this.PAGINA);
  }

  // ─── acordeón (colapsar ciclos) ─────────────────────────────

  estaExpandido(clave: string): boolean {
    return this.expandidos().has(clave);
  }

  toggleCiclo(clave: string): void {
    this.expandidos.update((set) => {
      const next = new Set(set);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });
  }

  // Cantidad de pasos (downlinks) del ciclo, para el resumen del header colapsado.
  pasosDelCiclo(eje: EjecucionCronologia): number {
    return eje.ramas.reduce((acc, r) => acc + r.pasos.length, 0);
  }

  diffsAbierto(id?: string): boolean {
    return !!id && this.diffsAbiertos().has(id);
  }

  toggleDiffs(id?: string): void {
    if (!id) return;
    this.diffsAbiertos.update((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Valor de un diff a texto compacto para mostrar esperado/real.
  valorDiff(v: unknown): string {
    if (v == null) return 'Sin valor';
    if (typeof v === 'object') {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  }

  // ─── header: estado de la config deseada ────────────────────

  get estadoConfig(): string | undefined {
    return this.configDeseada?.estado;
  }

  get diffsCount(): number {
    return this.configDeseada?.diffs?.length ?? 0;
  }

  get reintentos(): number {
    return this.configDeseada?.reintentosReconciliacion ?? 0;
  }

  get bloqueadoMin(): number | null {
    const hasta = this.configDeseada?.bloqueadoHasta;
    if (!hasta) return null;
    const ms = new Date(hasta).getTime() - Date.now();
    if (!isFinite(ms) || ms <= 0) return null;
    return Math.ceil(ms / 60000);
  }

  estadoConfigTono(): TonoEstado {
    switch (this.estadoConfig) {
      case 'Coincide':
        return 'ok';
      case 'No coincide':
        return 'alerta';
      default:
        return 'advertencia';
    }
  }

  toggleAyuda(): void {
    this.mostrarAyuda.update((v) => !v);
  }

  abrirConfigReal(): void {
    this.formularios.abrir(ConfigDeseadaDialogComponent, {
      tamano: 'grande',
      data: {
        luminaria: this.luminaria,
        configDeseada: this.configDeseada,
      },
      cerrarAfuera: true,
    });
  }
}
